import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useSocket } from "@/hooks/useSocket";
import { MapView, type EllaMapController } from "@/components/Map";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Shield, Radio, MapPin, Camera, Mic, Wifi, WifiOff,
  QrCode, Power, Clock, Navigation, Eye, Activity,
  ChevronLeft, Plus, Signal, Smartphone, Volume2,
  AlertTriangle, CheckCircle2, XCircle, Loader2, ScanFace,
  User, Crosshair, FileText, Video, X as XIcon, Download, FileWarning
} from "lucide-react";
import QRCodeLib from "qrcode";
import CommandNav from "@/components/CommandNav";
import { ACTIVE_CAMERA_LIMIT, addCameraSelection, buildActiveCameraRequest } from "@/lib/activeMonitor";

function playOperationalAlertTone() {
  try {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 720;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.18);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.2);
    window.setTimeout(() => context.close().catch(() => {}), 350);
  } catch {}
}

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  speed?: number;
  heading?: number;
  timestamp: string;
}

interface EventItem {
  eventType: string;
  message: string;
  createdAt: string;
}

interface PermissionData {
  type: "camera" | "microphone" | "location";
  enabled: boolean;
}

interface ActiveCameraFeedData {
  frame: string;
  socketId: string;
  clientId?: string;
  eventId?: number | null;
  reportedBy: string;
  timestamp: number;
}

interface SceneAnalysisData {
  priority: string;
  situation: string;
  threats: string[];
  recommendation: string;
  requiresMedical: boolean;
  requiresFireDept: boolean;
  confidence: number;
  description: string;
}

interface ActiveAudioData {
  audioUrl?: string;
  audioChunk?: string;
  socketId: string;
  clientId?: string;
  eventId?: number | null;
  reportedBy: string;
  timestamp: number;
  mimeType?: string;
}

interface ActiveTripData {
  tripId: number;
  reportedBy: string;
  destinationLabel: string;
  travelMode?: "walking" | "car";
  originLat?: number;
  originLng?: number;
  destLat?: number;
  destLng?: number;
  lat?: number;
  lng?: number;
  distToDestMeters?: number;
  etaAt?: number;
  alertType?: string | null;
}

interface ActiveVictimData {
  socketId: string;
  sessionCode?: string;
  clientId?: string;
  name: string;
  sector: string;
  location: { lat: number; lng: number } | null;
  connectedAt: number;
  lastUpdate: number;
  eventId: number | null;
  hasCheckin?: boolean;
  personId?: number | null;
  personPhotoUrl?: string | null;
  checkinPhotoUrl?: string | null;
}

interface FaceRecognitionData {
  eventId: number;
  matched: boolean;
  matchLevel: "high" | "medium" | "low" | "none";
  personId: number | null;
  personName: string | null;
  personRole: string | null;
  personPhotoUrl: string | null;
  personCategory: string | null;
  confidence: number;
  capturedImageUrl: string | null;
  faceCount: number;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  sector: string;
  origin: string;
  deviceName: string;
  deviceBrowser: string;
  sessionCode: string;
  timestamp: string;
  status: string;
  classification: string;
}

interface ConflictRecognitionData {
  eventId: number;
  faceCount: number;
  person1: { matched: boolean; personId: number | null; personName: string | null; personCategory: string | null; confidence: number; capturedImageUrl: string | null; personPhotoUrl: string | null; role: string };
  person2: { matched: boolean; personId: number | null; personName: string | null; personCategory: string | null; confidence: number; capturedImageUrl: string | null; personPhotoUrl: string | null; role: string };
  classification: string;
  sector: string;
  timestamp: string;
  status: string;
}

export default function Dashboard() {
  const params = useParams<{ sessionId?: string }>();
  const [, navigate] = useLocation();
  const { socket, connected: socketConnected } = useSocket();

  const [activeSessionId, setActiveSessionId] = useState<number | null>(
    params.sessionId ? parseInt(params.sessionId) : null
  );
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [connectUrl, setConnectUrl] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<string>("waiting");
  const [deviceName, setDeviceName] = useState<string>("");
  const [deviceBrowser, setDeviceBrowser] = useState<string>("");
  const [location, setLocation] = useState<LocationData | null>(null);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [permissions, setPermissions] = useState({ camera: false, microphone: false, location: false });
  const [audioLevel, setAudioLevel] = useState(0);
  const [deviceOnline, setDeviceOnline] = useState(false);

  // Face recognition state
  const [lastRecognition, setLastRecognition] = useState<FaceRecognitionData | null>(null);
  const [recognitionHistory, setRecognitionHistory] = useState<FaceRecognitionData[]>([]);

  // Conflict detection state
  const [lastConflict, setLastConflict] = useState<ConflictRecognitionData | null>(null);
  const [conflictHistory, setConflictHistory] = useState<ConflictRecognitionData[]>([]);

  // Agent dispatch state (v6.0 enhanced)
  const [dispatchedAgent, setDispatchedAgent] = useState<{
    eventId: number;
    dispatchId?: number;
    agentId?: number;
    agentName: string;
    agentPhone: string;
    agentChannel?: string;
    classification?: string;
    classLabel?: string;
    agentLink?: string;
    fullAgentLink?: string;
    agentLinkToken?: string;
    status: string;
    statusLabel: string;
    dispatchedAt: Date;
  } | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<{ status: string; channel: string } | null>(null);
  const [agentMessageDraft, setAgentMessageDraft] = useState("");
  const [agentRealtimeMessages, setAgentRealtimeMessages] = useState<Array<{eventId:number;agentName:string;message:string;kind:string;timestamp:number}>>([]);
  const [agentSceneAnalysis, setAgentSceneAnalysis] = useState<any>(null);

  // V7.5: Live Operation Panel state
  const [liveOperation, setLiveOperation] = useState<{
    eventId: number | null;
    eventStatus: string;
    personName: string | null;
    personPhotoUrl: string | null;
    classification: string | null;
    confidence: number;
    agentName: string | null;
    agentStatus: string | null;
    agentStatusLabel: string | null;
    cameraActive: boolean;
    latestTreatment: string | null;
    sector: string | null;
    timestamp: string;
  }>({
    eventId: null, eventStatus: '', personName: null, personPhotoUrl: null,
    classification: null, confidence: 0, agentName: null, agentStatus: null,
    agentStatusLabel: null, cameraActive: false, latestTreatment: null,
    sector: null, timestamp: '',
  });

  // V8.5: Mobile app activity state
  const [mobileCheckins, setMobileCheckins] = useState<Array<{
    eventId: number | null;
    matched: boolean;
    personName: string;
    personCategory: string | null;
    personPhotoUrl: string | null;
    capturedImageUrl: string | null;
    confidence: number;
    sector: string;
    classification: string;
    timestamp: string;
  }>>([]);

  // V7.5: Agent location state (from agent device)
  const [agentLocation, setAgentLocation] = useState<{ lat: number; lng: number; timestamp: string } | null>(null);

  // V8.8: Mobile emergency tracking state
  const [mobileEmergency, setMobileEmergency] = useState<{
    eventId: number;
    reportedBy: string;
    sector: string;
    description: string;
    location: { lat: number; lng: number } | null;
    address: string;
    timestamp: string;
  } | null>(null);
  const [mobileEmergencyLocation, setMobileEmergencyLocation] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);

  // V9.0: Track all mobile emergencies and app activity tab
  const [mobileEmergencies, setMobileEmergencies] = useState<Array<{
    eventId: number;
    reportedBy: string;
    sector: string;
    description: string;
    timestamp: string;
    agentName?: string;
    status: string;
  }>>([]);
  const [activeVictims, setActiveVictims] = useState<ActiveVictimData[]>([]);
  const [activeTrips, setActiveTrips] = useState<ActiveTripData[]>([]);
  const [activeCameraFeeds, setActiveCameraFeeds] = useState<ActiveCameraFeedData[]>([]);
  const [activeCameraAudios, setActiveCameraAudios] = useState<ActiveAudioData[]>([]);
  const [activeCameraStatuses, setActiveCameraStatuses] = useState<Record<string, string>>({});
  const [activeSceneAnalyses, setActiveSceneAnalyses] = useState<Record<string, SceneAnalysisData>>({});
  const [selectedCameraIds, setSelectedCameraIds] = useState<string[]>([]);
  const [selectedActiveVictimId, setSelectedActiveVictimId] = useState<string | null>(null);
  const [activeVictimSearch, setActiveVictimSearch] = useState("");
  const [victimMessageDraft, setVictimMessageDraft] = useState("");
  const [appActivityTab, setAppActivityTab] = useState<'checkins' | 'emergencias' | 'logadas' | 'trajetos' | 'cameras'>('checkins');

  const requestActiveCamera = useCallback((victim: ActiveVictimData) => {
    if (!socket) return;
    socket.emit("dashboard:camera-request", buildActiveCameraRequest(victim.socketId, victim.clientId));
  }, [socket]);
  const requestAllActiveCameras = useCallback(() => {
    if (!socket || activeVictims.length === 0) return;
    const targets = activeVictims.slice(0, ACTIVE_CAMERA_LIMIT);
    targets.forEach(victim => socket.emit("dashboard:camera-request", buildActiveCameraRequest(victim.socketId, victim.clientId)));
    setSelectedCameraIds(targets.map(victim => victim.socketId));
    setAppActivityTab('cameras');
    toast.success(`Solicitação enviada para ${targets.length} pessoa(s). Cada celular deverá autorizar câmera e áudio.`);
  }, [socket, activeVictims]);
  const toggleCameraSelection = useCallback((socketId: string) => {
    setSelectedCameraIds(previous => previous.includes(socketId) ? previous.filter(id => id !== socketId) : addCameraSelection(previous, socketId));
  }, []);

  // V9.5: Mobile emergency camera feed and audio
  const [mobileEmergencyCameraFrame, setMobileEmergencyCameraFrame] = useState<string | null>(null);
  const [mobileEmergencyAudios, setMobileEmergencyAudios] = useState<Array<{ audioUrl: string; reportedBy: string; timestamp: number }>>([]);

  // V7.4: Agent camera live preview state
  const [agentCameraFrame, setAgentCameraFrame] = useState<string | null>(null);
  const [agentCameraEventId, setAgentCameraEventId] = useState<number | null>(null);
  const [agentCameraActive, setAgentCameraActive] = useState(false);
  const [showAgentCameraModal, setShowAgentCameraModal] = useState(false);
  const [showAppQrModal, setShowAppQrModal] = useState(false);
  const [agentCameraAgentName, setAgentCameraAgentName] = useState<string>("");
  const agentCameraTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const mapRef = useRef<EllaMapController | null>(null);

  useEffect(() => {
    if (agentLocation && mapRef.current) {
      const pos = { lat: agentLocation.lat, lng: agentLocation.lng };
      mapRef.current.setAgent(pos, "Agente em deslocamento");
      mapRef.current.fitOperationalBounds();
    }
  }, [agentLocation?.lat, agentLocation?.lng]);

  const createSession = trpc.session.create.useMutation();
  const endSession = trpc.session.end.useMutation();
  const { data: persistedActiveTrips } = trpc.trip.listActive.useQuery(undefined, { refetchInterval: 15000 });

  useEffect(() => {
    if (!persistedActiveTrips) return;
    setActiveTrips(persistedActiveTrips.map((trip: any) => ({
      tripId: trip.id,
      reportedBy: trip.reportedBy,
      destinationLabel: trip.destinationLabel,
      originLat: trip.originLat ? Number(trip.originLat) : undefined,
      originLng: trip.originLng ? Number(trip.originLng) : undefined,
      destLat: trip.destLat ? Number(trip.destLat) : undefined,
      destLng: trip.destLng ? Number(trip.destLng) : undefined,
      lat: trip.lastLat ? Number(trip.lastLat) : undefined,
      lng: trip.lastLng ? Number(trip.lastLng) : undefined,
      alertType: trip.alertType || null,
    })));
  }, [persistedActiveTrips]);

  // V7.8: Classificação assistida — confirmar/descartar agressor
  const confirmAggressorMut = trpc.opEvent.confirmAggressor.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.confirmed ? "Agressor confirmado pelo operador" : "Classificação descartada", { duration: 4000 });
      addEvent({
        eventType: vars.confirmed ? "aggressor_confirmed" : "aggressor_discarded",
        message: vars.confirmed ? `Agressor confirmado no evento #${vars.eventId}` : `Classificação descartada no evento #${vars.eventId}`,
        createdAt: new Date().toISOString(),
      });
    },
    onError: () => toast.error("Erro ao atualizar classificação"),
  });

  // V7.8: Marcar/desmarcar trote
  const markTroteMut = trpc.opEvent.markTrote.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.isTrote ? "Evento marcado como TROTE" : "Trote desmarcado", { duration: 4000 });
      addEvent({
        eventType: vars.isTrote ? "trote_marked" : "trote_unmarked",
        message: vars.isTrote ? `TROTE marcado no evento #${vars.eventId}` : `Trote desmarcado no evento #${vars.eventId}`,
        createdAt: new Date().toISOString(),
      });
    },
    onError: () => toast.error("Erro ao marcar trote"),
  });

  const addEvent = useCallback((evt: EventItem) => {
    setEvents(prev => [evt, ...prev].slice(0, 200));
  }, []);

  // Create new session
  const handleCreateSession = useCallback(async () => {
    try {
      const result = await createSession.mutateAsync();
      setActiveSessionId(result.session.id);
      // Always use the current browser origin for the connect URL
      // This fixes the issue where backend returns internal Cloud Run URLs
      const sessionCode = result.session.sessionCode;
      const correctUrl = `${window.location.origin}/connect/${encodeURIComponent(sessionCode)}`;
      setConnectUrl(correctUrl);
      // Regenerate QR Code with the correct public URL
      try {
        const correctQr = await QRCodeLib.toDataURL(correctUrl, { width: 400, margin: 2, color: { dark: "#1a1a2e", light: "#ffffff" } });
        setQrCode(correctQr);
      } catch {
        // Fallback to backend QR if frontend generation fails
        setQrCode(result.qrCode);
      }
      setSessionStatus("waiting");
      setEvents([]);
      setLocation(null);
      setDeviceOnline(false);
      setPermissions({ camera: false, microphone: false, location: false });
      setLastRecognition(null);
      setRecognitionHistory([]);

      if (socket) {
        socket.emit("dashboard:join", { sessionId: result.session.id });
      }

      addEvent({
        eventType: "session_created",
        message: "Sessão criada — QR code gerado",
        createdAt: new Date().toISOString(),
      });

      navigate(`/dashboard/${result.session.id}`);
      toast.success("Sessão criada com sucesso");
    } catch (error: any) {
      const detail = error?.message || error?.data?.message || "Falha desconhecida";
      console.error("[ELLA] Falha ao criar sessão", error);
      toast.error(`Erro ao criar sessão: ${detail}`, { duration: 10000 });
    }
  }, [createSession, socket, addEvent, navigate]);

  // End session
  const handleEndSession = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      if (socket) socket.emit("session:end", { sessionId: activeSessionId });
      await endSession.mutateAsync({ id: activeSessionId });
      setSessionStatus("ended");
      setDeviceOnline(false);
      if (peerRef.current) {
        peerRef.current.close();
        peerRef.current = null;
      }
      toast.info("Sessão encerrada");
    } catch {
      toast.error("Erro ao encerrar sessão");
    }
  }, [activeSessionId, socket, endSession]);

  // Global socket listeners (work without active session — for mobile app events)
  useEffect(() => {
    if (!socket) return;

    const onNewEvent = (evt: EventItem) => addEvent(evt);

    const onActiveVictimUpdate = (data: { victims?: ActiveVictimData[] }) => {
      const victims = Array.isArray(data.victims) ? data.victims : [];
      setActiveVictims(victims);
      setSelectedActiveVictimId(previous => previous && victims.some(victim => victim.socketId === previous) ? previous : null);
    };

    const onTripStarted = (data: any) => {
      setActiveTrips(previous => [...previous.filter(trip => trip.tripId !== data.tripId), {
        tripId: data.tripId,
        reportedBy: data.reportedBy || "Pessoa no App",
        destinationLabel: data.destinationLabel || "Destino não informado",
        travelMode: data.travelMode,
        originLat: data.originLat,
        originLng: data.originLng,
        destLat: data.destLat,
        destLng: data.destLng,
        lat: data.originLat,
        lng: data.originLng,
        distToDestMeters: data.distToDestMeters,
        etaAt: data.etaAt,
        alertType: null,
      }]);
    };
    const onTripUpdate = (data: any) => {
      setActiveTrips(previous => previous.map(trip => trip.tripId === data.tripId ? { ...trip, ...data } : trip));
    };
    const onTripAlert = (data: any) => {
      setActiveTrips(previous => previous.map(trip => trip.tripId === data.tripId ? { ...trip, alertType: data.type, lat: data.lat, lng: data.lng } : trip));
      playOperationalAlertTone();
      toast.error(`Alerta de trajeto: ${data.type || "atenção"} — ${data.reportedBy || "Pessoa no App"}`, { duration: 8000 });
    };
    const onTripFinished = (data: any) => {
      setActiveTrips(previous => previous.filter(trip => trip.tripId !== data.tripId));
      if (data.destinationLabel) toast.success(`Trajeto de ${data.reportedBy || "pessoa ativa"} encerrado`);
    };

    // V8.5: Mobile check-in result listener (global)
    const onMobileCheckinResult = (data: any) => {
      setMobileCheckins(prev => [{
        eventId: data.eventId,
        matched: data.matched,
        personName: data.personName || "Visitante",
        personCategory: data.personCategory,
        personPhotoUrl: data.personPhotoUrl,
        capturedImageUrl: data.capturedImageUrl,
        confidence: data.confidence || 0,
        sector: data.sector || "Geral",
        classification: data.classification || "unidentified_person",
        timestamp: data.timestamp || new Date().toISOString(),
      }, ...prev].slice(0, 100));
      if (data.classification === "suspect_identified") {
        toast.error(`AGRESSOR detectado no check-in: ${data.personName}`, { duration: 10000 });
      } else if (data.matched) {
        toast.success(`Check-in: ${data.personName} (${(data.confidence || 0).toFixed(0)}%)`, { duration: 5000 });
      } else {
        toast.info(`Check-in: Visitante — Setor ${data.sector}`, { duration: 3000 });
      }
    };

    // V9.1: Agent alert notification — no-op now, onAgentDispatched handles auto-open
    const onAgentAlert = (data: { eventId: number; agentName: string; agentPhone: string; message?: string; channel?: string }) => {
      // V10.1: Disabled — onAgentDispatched already auto-opens WhatsApp/SMS to avoid double-open
    };

    // V8.9: SMS auto-notification (legacy fallback — kept for backward compat)
    const onSmsAuto = (data: { eventId: number; agentName: string; agentPhone: string; message?: string }) => {
      // Only handle if agent-alert didn't already fire (avoid double-open)
      // This is a no-op now since agent-alert handles both channels
    };

    // V8.6: Agent dispatched (global — from mobile emergency)
    // V10.1: Auto-open WhatsApp/SMS when agent is dispatched
    const onAgentDispatched = (data: any) => {
      // Auto-open WhatsApp or SMS immediately
      const phone = (data.agentPhone || '').replace(/\D/g, '');
      const channel = data.agentChannel || 'sms';
      const fullUrl = data.fullAgentLink && data.fullAgentLink.startsWith('http') ? data.fullAgentLink : (data.fullAgentLink ? `${window.location.origin}${data.fullAgentLink}` : '');
      if (phone && fullUrl) {
        const msg = `ALERTA ELLA - ${data.classLabel || 'EMERGENCIA'}. Agente: ${data.agentName}. Acesse: ${fullUrl}`;
        if (channel === 'whatsapp') {
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
        } else {
          window.location.href = `sms:${phone}?body=${encodeURIComponent(msg)}`;
        }
        toast.success(`${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} aberto automaticamente para ${data.agentName}`, { duration: 5000 });
      }
      setDispatchedAgent({
        eventId: data.eventId,
        dispatchId: data.dispatchId,
        agentId: data.agentId,
        agentName: data.agentName,
        agentPhone: data.agentPhone || '',
        agentChannel: data.agentChannel || 'sms',
        classification: data.classification,
        classLabel: data.classLabel,
        agentLink: data.agentLink,
        fullAgentLink: data.fullAgentLink && data.fullAgentLink.startsWith('http') ? data.fullAgentLink : (data.fullAgentLink ? `${window.location.origin}${data.fullAgentLink}` : (data.agentLink ? `${window.location.origin}${data.agentLink}` : undefined)),
        agentLinkToken: data.agentLinkToken,
        status: 'dispatched',
        statusLabel: 'Acionado',
        dispatchedAt: new Date(),
      });
      setLiveOperation(prev => ({
        ...prev,
        eventId: data.eventId,
        agentName: data.agentName,
        agentStatus: 'dispatched',
        agentStatusLabel: 'Acionado',
        latestTreatment: `Agente ${data.agentName} acionado — ${data.classLabel || 'Identificação'}`,
      }));
      toast.success(`Agente ${data.agentName} acionado — ${data.classLabel || "Identificação"}`, {
        duration: 8000,
      });
      // V9.0: Update emergency with agent name if it's from mobile
      if (data.classification === 'emergency') {
        setMobileEmergencies(prev => prev.map(e => e.eventId === data.eventId ? { ...e, agentName: data.agentName, status: 'agente_acionado' } : e));
      }
    };

    // V8.8: Emergency location from mobile app
    const onEmergencyLocation = (data: any) => {
      setMobileEmergency({
        eventId: data.eventId,
        reportedBy: data.reportedBy || "Usuário App",
        sector: data.sector || "Não informado",
        description: data.description || "Emergência",
        location: data.location,
        address: data.address || "",
        timestamp: data.timestamp || new Date().toISOString(),
      });
      if (data.location) {
        setMobileEmergencyLocation({ lat: data.location.lat, lng: data.location.lng, accuracy: 0 });
        // Auto-center map on emergency location
        if (mapRef.current) {
          const pos = { lat: data.location.lat, lng: data.location.lng };
          mapRef.current.setEmergency(pos, `Emergência: ${data.reportedBy}`);
          mapRef.current.panTo(pos); mapRef.current.setZoom(17);
        }
      }
      toast.error(`\u{1F6A8} EMERGÊNCIA: ${data.reportedBy} no setor ${data.sector}`, {
        duration: 30000,
        id: `emergency-${data.eventId}`,
        action: { label: "ABRIR EVENTO", onClick: () => navigate(`/events/${data.eventId}`) },
      });
      // V9.0: Add to mobile emergencies list
      setMobileEmergencies(prev => [{
        eventId: data.eventId,
        reportedBy: data.reportedBy || "Usuário App",
        sector: data.sector || "Não informado",
        description: data.description || "Emergência via App",
        timestamp: data.timestamp || new Date().toISOString(),
        status: "ativo",
      }, ...prev.filter(e => e.eventId !== data.eventId)]);
      setAppActivityTab('emergencias');
    };

    // V8.8: Continuous location updates from mobile during emergency
    const onLocationRelay = (data: { eventId: number; lat: number; lng: number; accuracy: number; reportedBy: string; timestamp: string }) => {
      setMobileEmergencyLocation({ lat: data.lat, lng: data.lng, accuracy: data.accuracy });
      // Update marker position on map
      if (mapRef.current) { const pos={lat:data.lat,lng:data.lng}; mapRef.current.setEmergency(pos, `Emergência: ${data.reportedBy || "Usuário"}`); mapRef.current.panTo(pos); }
    };

    socket.on("event:new", onNewEvent);
    socket.on("victim:active-update", onActiveVictimUpdate);
    socket.emit("get-active-victims");
    socket.on("trip:started", onTripStarted);
    socket.on("trip:update", onTripUpdate);
    socket.on("trip:alert", onTripAlert);
    socket.on("trip:arrived", onTripFinished);
    socket.on("trip:ended", onTripFinished);
    socket.on("mobile:checkin-result", onMobileCheckinResult);
    socket.on("notification:sms-auto", onSmsAuto);
    socket.on("notification:agent-alert", onAgentAlert);
    socket.on("agent:dispatched", onAgentDispatched);
    socket.on("mobile:emergency-location", onEmergencyLocation);
    socket.on("mobile:location-relay", onLocationRelay);

    // V9.5/Fase 4: camera, frames e áudio com identidade por socket/clientId.
    const onMobileCameraFeed = (data: ActiveCameraFeedData) => {
      setMobileEmergencyCameraFrame(data.frame);
      if (data.socketId) setActiveCameraFeeds(previous => [{ ...data }, ...previous.filter(item => item.socketId !== data.socketId)].slice(0, 20));
    };
    const onMobileAudioReceived = (data: ActiveAudioData) => {
      setMobileEmergencyAudios(prev => [...prev.slice(-9), { audioUrl: data.audioUrl || "", reportedBy: data.reportedBy, timestamp: data.timestamp }]);
      if (data.socketId) setActiveCameraAudios(previous => [data, ...previous.filter(item => item.socketId !== data.socketId)].slice(0, 20));
      toast.info(`🎤 Áudio recebido de ${data.reportedBy}`, { duration: 4000 });
    };
    const onMobileAudioStream = (data: ActiveAudioData) => {
      if (data.socketId) setActiveCameraAudios(previous => [data, ...previous.filter(item => item.socketId !== data.socketId)].slice(0, 20));
    };
    const onMobileCameraRequestResult = (data: { ok: boolean; name?: string; message?: string; socketId?: string }) => {
      if (data.ok) toast.success(`Solicitação de câmera enviada para ${data.name || "pessoa ativa"}`);
      else toast.error(data.message || "Pessoa não está mais conectada");
    };
    const onMobileCameraStatus = (data: { socketId: string; status: string; name?: string; message?: string }) => {
      setActiveCameraStatuses(previous => ({ ...previous, [data.socketId]: data.status }));
      if (data.status === "streaming") toast.success(`Câmera de ${data.name || "pessoa ativa"} está ao vivo`);
      if (data.status === "error") toast.error(data.message || `Câmera de ${data.name || "pessoa ativa"} indisponível`);
    };
    const onSceneAnalysis = (data: { socketId: string; analysis: SceneAnalysisData }) => {
      if (data.socketId) setActiveSceneAnalyses(previous => ({ ...previous, [data.socketId]: data.analysis }));
    };
    const onSceneEscalation = (data: { socketId: string; escalations?: string[]; analysis?: SceneAnalysisData; reportedBy?: string }) => {
      if (data.socketId && data.analysis) setActiveSceneAnalyses(previous => ({ ...previous, [data.socketId]: data.analysis as SceneAnalysisData }));
      const severity = data.analysis?.priority === "critical" ? "error" : "warning";
      const message = `IA ${data.analysis?.priority || "atenção"}: ${data.analysis?.situation || "escalada operacional"} — ${data.reportedBy || "sessão ativa"}`;
      playOperationalAlertTone();
      severity === "error" ? toast.error(message, { duration: 10000 }) : toast.warning(message, { duration: 8000 });
    };
    socket.on("mobile:emergency-camera-feed", onMobileCameraFeed);
    socket.on("mobile:emergency-audio-received", onMobileAudioReceived);
    socket.on("mobile:audio-stream-chunk", onMobileAudioStream);
    socket.on("dashboard:mobile-camera-request-result", onMobileCameraRequestResult);
    socket.on("dashboard:mobile-camera-status", onMobileCameraStatus);
    socket.on("scene:analysis-result", onSceneAnalysis);
    socket.on("scene:escalation", onSceneEscalation);

    return () => {
      socket.off("event:new", onNewEvent);
      socket.off("victim:active-update", onActiveVictimUpdate);
      socket.off("trip:started", onTripStarted);
      socket.off("trip:update", onTripUpdate);
      socket.off("trip:alert", onTripAlert);
      socket.off("trip:arrived", onTripFinished);
      socket.off("trip:ended", onTripFinished);
      socket.off("mobile:checkin-result", onMobileCheckinResult);
      socket.off("notification:sms-auto", onSmsAuto);
      socket.off("notification:agent-alert", onAgentAlert);
      socket.off("agent:dispatched", onAgentDispatched);
      socket.off("mobile:emergency-location", onEmergencyLocation);
      socket.off("mobile:location-relay", onLocationRelay);
      socket.off("mobile:emergency-camera-feed", onMobileCameraFeed);
      socket.off("mobile:emergency-audio-received", onMobileAudioReceived);
      socket.off("mobile:audio-stream-chunk", onMobileAudioStream);
      socket.off("dashboard:mobile-camera-request-result", onMobileCameraRequestResult);
      socket.off("dashboard:mobile-camera-status", onMobileCameraStatus);
      socket.off("scene:analysis-result", onSceneAnalysis);
      socket.off("scene:escalation", onSceneEscalation);
    };
  }, [socket, addEvent]);

  // Session-specific socket listeners (require active session)
  useEffect(() => {
    if (!socket || !activeSessionId) return;

    socket.emit("dashboard:join", { sessionId: activeSessionId });

    const onSessionUpdated = (data: any) => {
      if (data.status) setSessionStatus(data.status);
      if (data.deviceName) setDeviceName(data.deviceName);
      if (data.deviceBrowser) setDeviceBrowser(data.deviceBrowser);
      if (data.status === "active") setDeviceOnline(true);
    };

    const onLocationUpdated = (data: LocationData) => {
      setLocation(data);
      if (mapRef.current) {
        const pos = { lat: data.latitude, lng: data.longitude };
        mapRef.current.setDevice(pos, "Dispositivo / pessoa acompanhada");
        mapRef.current.appendTrail(pos); mapRef.current.panTo(pos);
      }
    };

    const onPermissionUpdated = (data: PermissionData) => {
      setPermissions(prev => ({ ...prev, [data.type]: data.enabled }));
    };

    const onAudioLevel = (data: { level: number }) => setAudioLevel(data.level);
    const onDeviceDisconnected = () => {
      setDeviceOnline(false);
      toast.warning("Dispositivo desconectado");
    };
    const onSessionEnded = () => {
      setSessionStatus("ended");
      setDeviceOnline(false);
    };

    // Face recognition event from backend
    const onFaceRecognition = (data: FaceRecognitionData) => {
      setLastRecognition(data);
      setRecognitionHistory(prev => [data, ...prev].slice(0, 50));

      // V7.5: Update live operation panel
      setLiveOperation(prev => ({
        ...prev,
        eventId: data.eventId,
        eventStatus: data.status || 'detected',
        personName: data.personName,
        personPhotoUrl: data.personPhotoUrl || data.capturedImageUrl,
        classification: data.classification,
        confidence: data.confidence,
        sector: data.sector,
        timestamp: data.timestamp,
        latestTreatment: data.matched
          ? `Pessoa identificada: ${data.personName} (${data.confidence.toFixed(0)}%)`
          : 'Face detectada — pessoa não identificada',
      }));

      if (data.matched) {
        toast.success(`Pessoa identificada: ${data.personName} (${data.confidence.toFixed(0)}%)`, {
          duration: 6000,
        });
      } else if (data.matchLevel === "low") {
        toast.warning(`Possível correspondência detectada — requer validação`, {
          duration: 6000,
        });
      } else {
        toast.info(`Face detectada — pessoa não identificada. Evento #${data.eventId} criado.`, {
          duration: 4000,
        });
      }
    };

    // Agent status update from mobile (v6.0)
    const onAgentStatusUpdate = (data: any) => {
      setDispatchedAgent(prev => prev ? {
        ...prev,
        status: data.status,
        statusLabel: data.statusLabel,
      } : prev);
      // V7.5: Update live operation panel
      setLiveOperation(prev => ({
        ...prev,
        agentStatus: data.status,
        agentStatusLabel: data.statusLabel,
        eventStatus: data.status === 'resolved' ? 'resolved' : prev.eventStatus,
        latestTreatment: `Agente ${data.agentName}: ${data.statusLabel}`,
      }));
      const statusToast: Record<string, () => void> = {
        accepted: () => toast.success(`Agente ${data.agentName}: Ocorrência aceita`),
        en_route: () => toast.info(`Agente ${data.agentName}: Em deslocamento`),
        on_scene: () => toast.success(`Agente ${data.agentName}: No local — em atendimento`),
        monitoring: () => toast.info(`Agente ${data.agentName}: Monitorando`),
        resolved: () => toast.success(`Agente ${data.agentName}: Ocorrência resolvida`),
      };
      (statusToast[data.status] || (() => toast.info(`Agente: ${data.statusLabel}`)))();
    };

    // Conflict recognition event from backend
    const onConflictRecognition = (data: ConflictRecognitionData) => {
      setLastConflict(data);
      setConflictHistory(prev => [data, ...prev].slice(0, 50));
      // Also add to recognition history for unified view
      const p1Label = data.person1.matched ? data.person1.personName : "Desconhecido";
      const p2Label = data.person2.matched ? data.person2.personName : "Desconhecido";
      toast.error(
        `⚠️ CONFLITO DETECTADO: ${p1Label} (${data.person1.role === "aggressor" ? "Agressor" : "Vítima"}) vs ${p2Label} (${data.person2.role === "aggressor" ? "Agressor" : "Vítima"})`,
        { duration: 10000 }
      );
    };

    // Dispatch failed (v6.0)
    const onDispatchFailed = (data: any) => {
      toast.error(`Falha no acionamento: ${data.message}`, { duration: 10000 });
    };

    // Notification status (v6.0)
    const onNotificationStatus = (data: any) => {
      setNotificationStatus({ status: data.status, channel: data.channel });
      if (data.status === "sent") {
        toast.info("Notificação enviada ao operador");
      } else {
        toast.warning("Falha no envio de notificação — contate o agente manualmente");
      }
    };

    // WebRTC: receive offer from device
    const onWebRTCOffer = async (data: { offer: RTCSessionDescriptionInit }) => {
      try {
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
          ],
        });
        peerRef.current = pc;

        pc.ontrack = (event) => {
          if (videoRef.current && event.streams[0]) {
            videoRef.current.srcObject = event.streams[0];
          }
        };

        pc.onicecandidate = (event) => {
          if (event.candidate && socket) {
            socket.emit("webrtc:ice-candidate", {
              sessionId: activeSessionId,
              candidate: event.candidate,
            });
          }
        };

        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit("webrtc:answer", {
          sessionId: activeSessionId,
          answer: answer,
        });

        addEvent({
          eventType: "video_connected",
          message: "Streaming de vídeo iniciado",
          createdAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error("WebRTC error:", err);
      }
    };

    const onICECandidate = async (data: { candidate: RTCIceCandidateInit }) => {
      if (peerRef.current) {
        try {
          await peerRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch (err) {
          console.error("ICE candidate error:", err);
        }
      }
    };

    socket.on("session:updated", onSessionUpdated);
    socket.on("location:updated", onLocationUpdated);
    socket.on("permission:updated", onPermissionUpdated);
    socket.on("audio:level", onAudioLevel);
    socket.on("device:disconnected", onDeviceDisconnected);
    socket.on("session:ended", onSessionEnded);
    socket.on("face:recognition", onFaceRecognition);
    socket.on("face:conflict-recognition", onConflictRecognition);
    socket.on("agent:status-update", onAgentStatusUpdate);
    socket.on("dispatch:failed", onDispatchFailed);
    socket.on("notification:status", onNotificationStatus);
    socket.on("webrtc:offer", onWebRTCOffer);
    socket.on("webrtc:ice-candidate", onICECandidate);

    // V7.5: Agent scan result — update live operation panel
    // V9.2: Qualquer detecção (matched ou não) é possível agressor
    const onAgentScanResult = (data: { eventId: number; agentName: string; matched: boolean; personName: string | null; classification: string; confidence: number; frameUrl: string | null; timestamp: string }) => {
      const classLabel = data.classification === 'aggressor' ? 'AGRESSOR CONFIRMADO' :
        data.classification === 'possible_aggressor' ? 'POSSÍVEL AGRESSOR' :
        data.classification === 'victim' ? 'Vítima' : 'Pessoa detectada';
      const personLabel = data.personName || 'Desconhecido';
      setLiveOperation(prev => ({
        ...prev,
        latestTreatment: `Câmera do agente: ${classLabel} — ${personLabel}${data.confidence > 0 ? ` (${data.confidence.toFixed(0)}%)` : ''}`,
      }));
      addEvent({
        eventType: data.classification === 'aggressor' || data.classification === 'possible_aggressor' ? 'aggressor_detected' : 'agent_scan_match',
        message: `Agente ${data.agentName}: ${classLabel} — ${personLabel}`,
        createdAt: data.timestamp || new Date().toISOString(),
      });
    };

    // V7.5: Agent location relay
    const onAgentLocationRelay = (data: { eventId: number; agentName: string; latitude: number; longitude: number; timestamp: string }) => {
      setAgentLocation({ lat: data.latitude, lng: data.longitude, timestamp: data.timestamp });
      setLiveOperation(prev => ({
        ...prev,
        latestTreatment: prev.latestTreatment || `Agente ${data.agentName} — localização atualizada`,
      }));
    };

    // V7.4: Agent camera frame relay for live preview
    const onAgentCameraFrame = (data: { eventId: number; agentName: string; frameBase64: string; timestamp: number }) => {
      setAgentCameraFrame(data.frameBase64);
      setAgentCameraEventId(data.eventId);
      setAgentCameraAgentName(data.agentName);
      setAgentCameraActive(true);
      // Auto-open modal when first frame arrives
      setShowAgentCameraModal(true);
      // Auto-hide after 10s of no frames
      if (agentCameraTimerRef.current) clearTimeout(agentCameraTimerRef.current);
      agentCameraTimerRef.current = setTimeout(() => {
        setAgentCameraActive(false);
      }, 10000);
    };

    // V7.4: Agent camera status update
    const onAgentCameraStatus = (data: { eventId: number; status: string }) => {
      if (data.status === "success" || data.status === "stream_acquired") {
        setAgentCameraActive(true);
        setAgentCameraEventId(data.eventId);
        setLiveOperation(prev => ({ ...prev, cameraActive: true }));
      } else if (data.status === "failed") {
        setAgentCameraActive(false);
        setLiveOperation(prev => ({ ...prev, cameraActive: false }));
      }
    };

    // V7.4: Agent released notification
    const onAgentReleased = (data: { eventId: number; agentName: string; agentId: number; reason: string }) => {
      toast.success(`Agente ${data.agentName} liberado automaticamente`, { duration: 5000 });
      setDispatchedAgent(prev => prev ? { ...prev, status: "resolved", statusLabel: "Disponível" } : prev);
      setAgentCameraActive(false);
      setShowAgentCameraModal(false);
      setLiveOperation(prev => ({
        ...prev,
        agentStatus: 'available',
        agentStatusLabel: 'Disponível',
        cameraActive: false,
        eventStatus: 'resolved',
        latestTreatment: `Agente ${data.agentName} liberado — evento encerrado`,
      }));
      setAgentLocation(null);
      addEvent({
        eventType: "agent_released",
        message: `Agente ${data.agentName} liberado — voltou para disponível (${data.reason})`,
        createdAt: new Date().toISOString(),
      });
    };

    socket.on("agent:camera-frame-relay", onAgentCameraFrame);
    socket.on("agent:camera-status", onAgentCameraStatus);
    socket.on("agent:released", onAgentReleased);
    socket.on("agent:scan-result", onAgentScanResult);
    socket.on("agent:location-relay", onAgentLocationRelay);
    const onAgentMessage=(data:any)=>{setAgentRealtimeMessages(prev=>[...prev.slice(-99),data]);if(data.kind==='signal')toast.warning(`${data.agentName}: ${data.message}`);};
    const onAgentScene=(data:any)=>{setAgentSceneAnalysis(data);if(data.priority==='critical')toast.error(`IA CRÍTICA — ${data.agentName}: ${data.situation}`,{duration:10000});else if(data.priority==='high')toast.warning(`IA ALTA — ${data.agentName}: ${data.situation}`,{duration:8000});};
    socket.on("agent:message-to-dashboard",onAgentMessage); socket.on("agent:scene-analysis",onAgentScene);



    return () => {
      socket.off("session:updated", onSessionUpdated);
      socket.off("location:updated", onLocationUpdated);
      socket.off("permission:updated", onPermissionUpdated);
      socket.off("audio:level", onAudioLevel);
      socket.off("device:disconnected", onDeviceDisconnected);
      socket.off("session:ended", onSessionEnded);
      socket.off("face:recognition", onFaceRecognition);
      socket.off("face:conflict-recognition", onConflictRecognition);
      socket.off("agent:status-update", onAgentStatusUpdate);
      socket.off("dispatch:failed", onDispatchFailed);
      socket.off("notification:status", onNotificationStatus);
      socket.off("webrtc:offer", onWebRTCOffer);
      socket.off("webrtc:ice-candidate", onICECandidate);
      socket.off("agent:camera-frame-relay", onAgentCameraFrame);
      socket.off("agent:camera-status", onAgentCameraStatus);
      socket.off("agent:released", onAgentReleased);
      socket.off("agent:scan-result", onAgentScanResult);
      socket.off("agent:location-relay", onAgentLocationRelay);
      socket.off("agent:message-to-dashboard",onAgentMessage); socket.off("agent:scene-analysis",onAgentScene);
      if (agentCameraTimerRef.current) clearTimeout(agentCameraTimerRef.current);
    };
  }, [socket, activeSessionId, addEvent]);

  const statusColor = sessionStatus === "active" ? "text-green-400" : sessionStatus === "ended" ? "text-red-400" : "text-yellow-400";
  const statusLabel = sessionStatus === "active" ? "Ativa" : sessionStatus === "ended" ? "Encerrada" : "Aguardando";

  const eventIcon = (type: string) => {
    if (type.includes("mobile_checkin") || type.includes("checkin")) return <ScanFace className="w-3.5 h-3.5 text-cyan-400" />;
    if (type.includes("aggressor_checkin")) return <AlertTriangle className="w-3.5 h-3.5 text-red-400 animate-pulse" />;
    if (type.includes("mobile_emergency") || type.includes("emergency")) return <AlertTriangle className="w-3.5 h-3.5 text-red-400 animate-pulse" />;
    if (type.includes("aggressor_report")) return <FileWarning className="w-3.5 h-3.5 text-amber-400" />;
    if (type.includes("face") || type.includes("recognition") || type.includes("facial")) return <ScanFace className="w-3.5 h-3.5 text-purple-400" />;
    if (type.includes("agent_released")) return <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />;
    if (type.includes("agent_dispatched") || type.includes("dispatch")) return <Shield className="w-3.5 h-3.5 text-purple-400" />;
    if (type.includes("victim_found")) return <Eye className="w-3.5 h-3.5 text-amber-400" />;
    if (type.includes("operational_event")) return <FileText className="w-3.5 h-3.5 text-amber-400" />;
    if (type.includes("connected") || type.includes("enabled") || type.includes("autorizado")) return <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />;
    if (type.includes("disconnected") || type.includes("disabled") || type.includes("negado") || type.includes("ended")) return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    if (type.includes("error") || type.includes("warning")) return <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />;
    return <Activity className="w-3.5 h-3.5 text-ella-purple-light" />;
  };

  const mapCenter = useMemo(() => location ? { lat: location.latitude, lng: location.longitude } : { lat: -23.6, lng: -46.7 }, []);

  const classificationLabel = (c: string) => {
    const map: Record<string, string> = {
      suspect_identified: "Agressor Identificado",
      victim_identified: "Vítima Identificada",
      identified_person: "Pessoa Identificada",
      unidentified_person: "Não Identificada",
      monitored_person: "Pessoa Monitorada",
      monitored: "Monitorado",
      under_analysis: "Em Análise",
      confirmed_incident: "Incidente Confirmado",
    };
    return map[c] || c;
  };

  const classificationColor = (c: string) => {
    const map: Record<string, string> = {
      suspect_identified: "text-red-400",
      victim_identified: "text-amber-400",
      confirmed_incident: "text-red-400",
      under_analysis: "text-slate-400",
    };
    return map[c] || "text-muted-foreground";
  };

  const agentStatusColor = (s: string) => {
    const map: Record<string, string> = {
      dispatched: "bg-red-500/20 text-red-400 border-red-500/30",
      accepted: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      en_route: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      on_scene: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      monitoring: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      resolved: "bg-green-500/20 text-green-400 border-green-500/30",
    };
    return map[s] || "bg-secondary text-muted-foreground";
  };

  const matchLevelBadge = (level: string) => {
    if (level === "high") return <Badge className="text-[9px] bg-green-500/20 text-green-400 border-green-500/30">Alta Confiança</Badge>;
    if (level === "medium") return <Badge className="text-[9px] bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Média Confiança</Badge>;
    if (level === "low") return <Badge className="text-[9px] bg-orange-500/20 text-orange-400 border-orange-500/30">Requer Validação</Badge>;
    return <Badge variant="secondary" className="text-[9px]">Não Identificado</Badge>;
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Header — ELLA Command Platform */}
      <CommandNav
        pageTitle="Dashboard"
        statusIndicator={{ connected: socketConnected }}
        rightContent={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAppActivityTab('logadas')}
              className="text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/10 text-xs"
            >
              <Smartphone className="w-3.5 h-3.5 mr-1" />
              Logadas ({activeVictims.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAppActivityTab('trajetos')}
              className="text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/10 text-xs"
            >
              <Navigation className="w-3.5 h-3.5 mr-1" />
              Trajetos ({activeTrips.length})
            </Button>
            {activeSessionId && sessionStatus !== "ended" && (
              <Button variant="outline" size="sm" onClick={handleEndSession} className="text-red-400 border-red-400/30 hover:bg-red-400/10 text-xs">
                <Power className="w-3.5 h-3.5 mr-1" />
                Encerrar
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowAppQrModal(true)} className="text-purple-400 border-purple-400/30 hover:bg-purple-400/10 text-xs">
              <Download className="w-3.5 h-3.5 mr-1" />
              App ELLA
            </Button>
            <Button size="sm" onClick={handleCreateSession} style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #ec4899 100%)" }} className="text-white border-0 hover:opacity-90 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" />
              Nova Sessão
            </Button>
          </>
        }
      />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel - Map + Video */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top row - Video + QR/Recognition */}
          <div className="flex gap-3 p-3 shrink-0" style={{ height: "280px" }}>
            {/* Video player */}
            <div className="flex-1 rounded-xl border border-border/50 bg-card overflow-hidden relative">
              <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm">
                <Camera className="w-3 h-3 text-white" />
                <span className="text-[10px] text-white font-medium">VÍDEO AO VIVO</span>
                {permissions.camera && deviceOnline && <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
              </div>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover bg-black"
              />
              {!permissions.camera && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
                  <Camera className="w-10 h-10 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground">Aguardando câmera</span>
                </div>
              )}
            </div>

            {/* QR Code / Face Recognition Card */}
            <div className="w-72 rounded-xl border border-border/50 bg-card flex flex-col shrink-0 overflow-hidden">
              {lastConflict ? (
                <div className="flex-1 p-3 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-red-400 uppercase tracking-wider font-bold animate-pulse">CONFLITO DETECTADO</span>
                    <Badge className="text-[9px] bg-red-500/20 text-red-400 border-red-500/30">{lastConflict.faceCount} faces</Badge>
                  </div>
                  {/* Two persons side by side */}
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <div className={`text-[9px] font-bold mb-1 ${lastConflict.person1.role === "aggressor" ? "text-red-400" : "text-blue-400"}`}>
                        {lastConflict.person1.role === "aggressor" ? "AGRESSOR" : "VÍTIMA"}
                      </div>
                      <div className="aspect-square rounded-lg overflow-hidden bg-secondary border-2 border-red-500/30">
                        {lastConflict.person1.capturedImageUrl ? (
                          <img src={lastConflict.person1.capturedImageUrl} alt="Pessoa 1" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><User className="w-6 h-6 text-muted-foreground/30" /></div>
                        )}
                      </div>
                      <div className="text-[9px] font-medium mt-1 truncate">{lastConflict.person1.matched ? lastConflict.person1.personName : "Desconhecido"}</div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-red-400 text-lg font-bold">VS</span>
                    </div>
                    <div className="flex-1">
                      <div className={`text-[9px] font-bold mb-1 ${lastConflict.person2.role === "aggressor" ? "text-red-400" : "text-blue-400"}`}>
                        {lastConflict.person2.role === "aggressor" ? "AGRESSOR" : "VÍTIMA"}
                      </div>
                      <div className="aspect-square rounded-lg overflow-hidden bg-secondary border-2 border-blue-500/30">
                        {lastConflict.person2.capturedImageUrl ? (
                          <img src={lastConflict.person2.capturedImageUrl} alt="Pessoa 2" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><User className="w-6 h-6 text-muted-foreground/30" /></div>
                        )}
                      </div>
                      <div className="text-[9px] font-medium mt-1 truncate">{lastConflict.person2.matched ? lastConflict.person2.personName : "Desconhecido"}</div>
                    </div>
                  </div>
                  {/* V7.8: Confirm/Discard aggressor buttons */}
                  <div className="flex gap-1.5 mt-2">
                    <button
                      onClick={() => {
                        confirmAggressorMut.mutate({ eventId: lastConflict.eventId, personField: "person1", confirmed: true });
                      }}
                      className="flex-1 text-[9px] py-1.5 rounded bg-red-500/15 border border-red-500/25 text-red-300 hover:bg-red-500/25 cursor-pointer font-semibold"
                    >
                      Confirmar Agressor
                    </button>
                    <button
                      onClick={() => {
                        confirmAggressorMut.mutate({ eventId: lastConflict.eventId, personField: "person1", confirmed: false });
                      }}
                      className="flex-1 text-[9px] py-1.5 rounded bg-slate-500/15 border border-slate-500/25 text-slate-300 hover:bg-slate-500/25 cursor-pointer"
                    >
                      Descartar
                    </button>
                  </div>
                  {/* V7.8: Mark as trote */}
                  <button
                    onClick={() => {
                      markTroteMut.mutate({ eventId: lastConflict.eventId, isTrote: true });
                    }}
                    className="w-full mt-1 text-[9px] py-1 rounded bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20 cursor-pointer"
                  >
                    Marcar como TROTE
                  </button>
                  <Button size="sm" variant="outline" className="w-full mt-1 text-[10px] h-7 border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => navigate(`/events/${lastConflict.eventId}`)}>
                    <FileText className="w-3 h-3 mr-1" /> Ver Conflito #{lastConflict.eventId}
                  </Button>
                </div>
              ) : lastRecognition ? (
                <div className="flex-1 p-3 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Reconhecimento Facial</span>
                    {matchLevelBadge(lastRecognition.matchLevel)}
                  </div>

                  {/* Photos side by side */}
                  <div className="flex gap-2 mb-2">
                    {lastRecognition.personPhotoUrl && (
                      <div className="flex-1">
                        <div className="text-[9px] text-muted-foreground mb-1">Cadastrada</div>
                        <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border/50">
                          <img src={lastRecognition.personPhotoUrl} alt="Cadastrada" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    )}
                    {lastRecognition.capturedImageUrl && (
                      <div className="flex-1">
                        <div className="text-[9px] text-muted-foreground mb-1">Capturada</div>
                        <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border/50">
                          <img src={lastRecognition.capturedImageUrl} alt="Capturada" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    )}
                    {!lastRecognition.personPhotoUrl && !lastRecognition.capturedImageUrl && (
                      <div className="flex-1 flex items-center justify-center py-4">
                        <User className="w-10 h-10 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>

                  {/* Person info */}
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold truncate">
                        {lastRecognition.matched ? lastRecognition.personName : "Não Identificado"}
                      </span>
                      <span className={`text-[10px] font-bold ${lastRecognition.confidence > 60 ? "text-green-400" : lastRecognition.confidence > 40 ? "text-yellow-400" : "text-red-400"}`}>
                        {lastRecognition.confidence > 0 ? `${lastRecognition.confidence.toFixed(0)}%` : "—"}
                      </span>
                    </div>
                    {lastRecognition.personRole && (
                      <div className="text-[10px] text-muted-foreground">{lastRecognition.personRole} — {lastRecognition.personCategory || "N/A"}</div>
                    )}
                    <div className="text-[10px] text-muted-foreground">
                      {lastRecognition.sector} — {new Date(lastRecognition.timestamp).toLocaleTimeString("pt-BR")}
                    </div>
                    <Badge variant="secondary" className="text-[9px]">{classificationLabel(lastRecognition.classification)}</Badge>
                  </div>

                  {/* View event button */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-2 text-[10px] h-7"
                    onClick={() => navigate(`/events/${lastRecognition.eventId}`)}
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Ver Evento #{lastRecognition.eventId}
                  </Button>
                </div>
              ) : qrCode ? (
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 font-medium">Escaneie para conectar</div>
                  <div className="rounded-lg overflow-hidden bg-white p-2 mb-2">
                    <img src={qrCode} alt="QR Code" className="w-36 h-36 object-contain" />
                  </div>
                  {connectUrl && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(connectUrl); toast.success("Link copiado!"); }}
                      className="text-[10px] text-ella-purple-light hover:underline cursor-pointer"
                    >
                      Copiar link
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                  <QrCode className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-xs text-muted-foreground text-center">Clique em "Nova Sessão" para gerar o QR code</p>
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="flex-1 p-3 pt-0">
            <div className="h-full rounded-xl border border-border/50 overflow-hidden relative">
              <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm">
                <MapPin className="w-3 h-3 text-white" />
                <span className="text-[10px] text-white font-medium">LOCALIZAÇÃO EM TEMPO REAL</span>
              </div>
              <MapView
                className="w-full h-full"
                initialCenter={mapCenter}
                initialZoom={15}
                liveCenter={location ? { lat: location.latitude, lng: location.longitude } : (mobileEmergencyLocation ? { lat: mobileEmergencyLocation.lat, lng: mobileEmergencyLocation.lng } : null)}
                onMapReady={(map) => {
                  mapRef.current = map;
                  if (mobileEmergencyLocation) {
                    const pos = { lat: mobileEmergencyLocation.lat, lng: mobileEmergencyLocation.lng };
                    map.setEmergency(pos, "Emergência Ativa"); map.panTo(pos); map.setZoom(17);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Right panel - Status + Recognition History + Events */}
        <div className="w-80 border-l border-border/50 flex flex-col bg-card/30 shrink-0">
          {/* Device status */}
          <div className="p-4 border-b border-border/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status do Dispositivo</h3>
              <Badge variant={deviceOnline ? "default" : "secondary"} className={`text-[10px] ${deviceOnline ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}`}>
                {deviceOnline ? "Online" : "Offline"}
              </Badge>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <Radio className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs">Sessão</span>
                </div>
                <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
              </div>

              {deviceName && (
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs">Dispositivo</span>
                  </div>
                  <span className="text-xs text-muted-foreground truncate max-w-[120px]">{deviceName}</span>
                </div>
              )}

              <div className="grid grid-cols-4 gap-1.5">
                <StatusPill active={permissions.camera} icon={<Camera className="w-3 h-3" />} label="Cam" />
                <StatusPill active={permissions.microphone} icon={<Mic className="w-3 h-3" />} label="Mic" />
                <StatusPill active={permissions.location} icon={<MapPin className="w-3 h-3" />} label="GPS" />
                <StatusPill active={socketConnected} icon={<Signal className="w-3 h-3" />} label="Net" />
              </div>

              {permissions.microphone && (
                <div className="flex items-center gap-2">
                  <Volume2 className="w-3 h-3 text-muted-foreground" />
                  <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-green-400 transition-all duration-100" style={{ width: `${Math.min(audioLevel * 100, 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* V7.5: Live Operation Panel — consolidated real-time status */}
          {liveOperation.eventId && (
            <div className="p-4 border-b border-border/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400">Operação em Andamento</h3>
                </div>
                <button
                  onClick={() => navigate(`/events/${liveOperation.eventId}`)}
                  className="text-[9px] text-purple-400 hover:underline cursor-pointer"
                >
                  #{liveOperation.eventId}
                </button>
              </div>
              <div className="space-y-2">
                {/* Person identified */}
                <div className="flex items-center gap-2 p-2 rounded bg-secondary/40">
                  {liveOperation.personPhotoUrl ? (
                    <img src={liveOperation.personPhotoUrl} alt="" className="w-8 h-8 rounded-full object-cover border border-border/50" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                      <User className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold truncate">{liveOperation.personName || 'Não identificado'}</div>
                    <div className="text-[9px] text-muted-foreground">
                      {liveOperation.classification ? classificationLabel(liveOperation.classification) : 'Detectado'}
                      {liveOperation.confidence > 0 && ` — ${liveOperation.confidence.toFixed(0)}%`}
                    </div>
                  </div>
                  {liveOperation.classification && (
                    <div className={`w-2 h-2 rounded-full ${liveOperation.classification.includes('suspect') || liveOperation.classification.includes('aggressor') ? 'bg-red-400' : liveOperation.classification.includes('victim') ? 'bg-amber-400' : 'bg-blue-400'}`} />
                  )}
                </div>

                {/* Agent + Camera status */}
                {liveOperation.agentName && (
                  <div className="flex items-center gap-2 p-2 rounded bg-secondary/40">
                    <Shield className="w-4 h-4 text-purple-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-semibold truncate">{liveOperation.agentName}</div>
                      <div className="text-[9px] text-muted-foreground">{liveOperation.agentStatusLabel || 'Acionado'}</div>
                    </div>
                    {liveOperation.cameraActive && (
                      <button
                        onClick={() => setShowAgentCameraModal(true)}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/25 cursor-pointer hover:bg-cyan-500/25"
                        title="Ver câmera ao vivo"
                      >
                        <Video className="w-3 h-3 text-cyan-400" />
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      </button>
                    )}
                  </div>
                )}

                {/* Agent location */}
                {agentLocation && (
                  <div className="flex items-center gap-2 p-2 rounded bg-secondary/40">
                    <MapPin className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono">{agentLocation.lat.toFixed(5)}, {agentLocation.lng.toFixed(5)}</div>
                      <div className="text-[9px] text-muted-foreground">Atualizado {new Date(agentLocation.timestamp).toLocaleTimeString('pt-BR')}</div>
                    </div>
                  </div>
                )}

                {/* V8.1: Victim/Aggressor names from conflict */}
                {lastConflict && (
                  <div className="flex gap-2">
                    <div className="flex-1 p-2 rounded bg-blue-500/10 border border-blue-500/20">
                      <div className="text-[9px] text-blue-300/80 uppercase tracking-wider font-semibold mb-0.5">Vítima</div>
                      <div className="text-[10px] text-blue-200 font-medium truncate">
                        {lastConflict.person1.role === 'victim' ? (lastConflict.person1.matched ? lastConflict.person1.personName : 'Não identificado') : (lastConflict.person2.matched ? lastConflict.person2.personName : 'Não identificado')}
                      </div>
                    </div>
                    <div className="flex-1 p-2 rounded bg-red-500/10 border border-red-500/20">
                      <div className="text-[9px] text-red-300/80 uppercase tracking-wider font-semibold mb-0.5">Agressor</div>
                      <div className="text-[10px] text-red-200 font-medium truncate">
                        {lastConflict.person1.role === 'aggressor' ? (lastConflict.person1.matched ? lastConflict.person1.personName : 'Não identificado') : (lastConflict.person2.matched ? lastConflict.person2.personName : 'Não identificado')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Latest treatment */}
                {liveOperation.latestTreatment && (
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                    <div className="text-[9px] text-amber-300/80 uppercase tracking-wider font-semibold mb-0.5">Última Tratativa</div>
                    <div className="text-[10px] text-amber-200 leading-snug">{liveOperation.latestTreatment}</div>
                  </div>
                )}

                {/* Sector + time */}
                <div className="flex items-center justify-between">
                  {liveOperation.sector && <span className="text-[9px] text-muted-foreground">{liveOperation.sector}</span>}
                  {liveOperation.timestamp && <span className="text-[9px] text-muted-foreground">{new Date(liveOperation.timestamp).toLocaleTimeString('pt-BR')}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Agent Dispatch Card (v6.0 enhanced + V8.1 SLA + status progression) */}
          {dispatchedAgent && (
            <div className="p-4 border-b border-border/30">
              {/* V8.1: Status progression bar */}
              <div className="flex items-center gap-0.5 mb-3">
                {['dispatched','accepted','en_route','on_scene','resolved'].map((step, idx) => {
                  const stepOrder = ['dispatched','accepted','en_route','on_scene','monitoring','resolved'];
                  const currentIdx = stepOrder.indexOf(dispatchedAgent.status);
                  const stepIdx = stepOrder.indexOf(step);
                  const isActive = stepIdx <= currentIdx;
                  const stepLabels: Record<string,string> = { dispatched:'Acionado', accepted:'Recebido', en_route:'A caminho', on_scene:'No local', resolved:'Finalizado' };
                  const stepColors: Record<string,string> = { dispatched:'bg-red-500', accepted:'bg-amber-500', en_route:'bg-cyan-500', on_scene:'bg-purple-500', resolved:'bg-green-500' };
                  return (
                    <div key={step} className="flex-1 flex flex-col items-center gap-0.5">
                      <div className={`w-full h-1.5 rounded-full ${isActive ? stepColors[step] || 'bg-green-500' : 'bg-secondary/50'}`} />
                      <span className={`text-[8px] ${isActive ? 'text-foreground font-semibold' : 'text-muted-foreground/50'}`}>{stepLabels[step]}</span>
                    </div>
                  );
                })}
              </div>
              {/* V8.1: SLA Timer */}
              <SLATimer startTime={dispatchedAgent.dispatchedAt} status={dispatchedAgent.status} />
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Agente Acionado</h3>
                  {/* V7.4: Camera active indicator */}
                  {agentCameraActive && dispatchedAgent.status !== "resolved" && (
                    <button
                      onClick={() => setShowAgentCameraModal(true)}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/25 cursor-pointer hover:bg-cyan-500/25 transition-colors"
                      title="Ver câmera do agente ao vivo"
                    >
                      <Video className="w-3 h-3 text-cyan-400" />
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    </button>
                  )}
                </div>
                <Badge className={`text-[9px] ${agentStatusColor(dispatchedAgent.status)} ${dispatchedAgent.status !== "resolved" ? "animate-pulse" : ""}`}>
                  {dispatchedAgent.statusLabel}
                </Badge>
              </div>
              <div className={`p-2.5 rounded-lg border ${
                dispatchedAgent.classification === "suspect_identified" ? "bg-red-500/10 border-red-500/20" :
                dispatchedAgent.classification === "victim_identified" ? "bg-amber-500/10 border-amber-500/20" :
                "bg-purple-500/10 border-purple-500/20"
              }`}>
                {/* Classification label */}
                {dispatchedAgent.classLabel && (
                  <div className={`text-[10px] font-bold mb-2 ${
                    dispatchedAgent.classification === "suspect_identified" ? "text-red-400" : "text-amber-400"
                  }`}>
                    {dispatchedAgent.classLabel}
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Shield className="w-3.5 h-3.5 text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">{dispatchedAgent.agentName}</p>
                    <p className="text-[9px] text-muted-foreground">{dispatchedAgent.agentPhone || "Sem telefone"}</p>
                  </div>
                  {/* Notification status indicator */}
                  {notificationStatus && (
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      notificationStatus.status === "sent" ? "bg-green-500/20" : "bg-red-500/20"
                    }`}>
                      {notificationStatus.status === "sent" 
                        ? <CheckCircle2 className="w-3 h-3 text-green-400" />
                        : <AlertTriangle className="w-3 h-3 text-red-400" />
                      }
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[9px] text-muted-foreground">
                    {new Date(dispatchedAgent.dispatchedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <button
                    onClick={() => navigate(`/events/${dispatchedAgent.eventId}`)}
                    className="text-[9px] text-purple-400 hover:underline cursor-pointer"
                  >
                    Evento #{dispatchedAgent.eventId}
                  </button>
                </div>
                {/* V9.1: Agent link + WhatsApp/SMS choice + Google Maps */}
                {dispatchedAgent.agentLink && (
                  <div className="mt-2 space-y-1.5">
                    <button
                       onClick={() => {
                         const fullUrl = dispatchedAgent.fullAgentLink || (dispatchedAgent.agentLink?.startsWith('http') ? dispatchedAgent.agentLink : `${window.location.origin}${dispatchedAgent.agentLink || ''}`);
                         navigator.clipboard.writeText(fullUrl);
                         toast.success("Link do agente copiado!");
                      }}
                      className="w-full text-[10px] text-center py-1.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-300 hover:bg-purple-500/20 transition-colors cursor-pointer"
                    >
                      Copiar Link do Agente
                    </button>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => {
                          const fullUrl = dispatchedAgent.fullAgentLink || (dispatchedAgent.agentLink?.startsWith('http') ? dispatchedAgent.agentLink : `${window.location.origin}${dispatchedAgent.agentLink || ''}`);
                          const phone = dispatchedAgent.agentPhone?.replace(/\D/g, '') || '';
                          const msg = `ALERTA ELLA - ${dispatchedAgent.classLabel || 'IDENTIFICAÇÃO'}. Agente: ${dispatchedAgent.agentName}. Acesse: ${fullUrl}`;
                          const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
                          window.open(waUrl, '_blank');
                          toast.success(`WhatsApp aberto para ${dispatchedAgent.agentName}`, { duration: 5000 });
                        }}
                        className="flex-1 text-[10px] text-center py-1.5 rounded bg-green-500/10 border border-green-500/20 text-green-300 hover:bg-green-500/20 transition-colors cursor-pointer"
                      >
                        WhatsApp
                      </button>
                      <button
                        onClick={() => {
                          const fullUrl = dispatchedAgent.fullAgentLink || (dispatchedAgent.agentLink?.startsWith('http') ? dispatchedAgent.agentLink : `${window.location.origin}${dispatchedAgent.agentLink || ''}`);
                          const phone = dispatchedAgent.agentPhone?.replace(/\D/g, '') || '';
                          const msg = `ALERTA ELLA - ${dispatchedAgent.classLabel || 'IDENTIFICAÇÃO'}. Agente: ${dispatchedAgent.agentName}. Acesse: ${fullUrl}`;
                          const smsUrl = `sms:${phone}?body=${encodeURIComponent(msg)}`;
                          window.location.href = smsUrl;
                          toast.success(`SMS aberto para ${dispatchedAgent.agentName}`, { duration: 5000 });
                        }}
                        className="flex-1 text-[10px] text-center py-1.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-300 hover:bg-blue-500/20 transition-colors cursor-pointer"
                      >
                        SMS
                      </button>
                    </div>
                  </div>
                )}
                {/* V9.1: Google Maps navigation link for agent */}
                {location && (
                  <button
                    onClick={() => {
                      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`;
                      window.open(mapsUrl, '_blank');
                      toast.success('Google Maps aberto com rota para o local');
                    }}
                    className="w-full mt-1.5 text-[10px] text-center py-1.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20 transition-colors cursor-pointer flex items-center justify-center gap-1"
                  >
                    <Navigation className="w-3 h-3" />
                    Abrir Rota no Google Maps
                  </button>
                )}
                {/* V7.4: Agent camera live view button */}
                {agentCameraActive && dispatchedAgent.status !== "resolved" && (
                  <button
                    onClick={() => setShowAgentCameraModal(true)}
                    className="w-full mt-2 text-[10px] text-center py-2 rounded bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 hover:bg-cyan-500/20 transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Video className="w-3.5 h-3.5" />
                    <span>Ver Câmera do Agente ao Vivo</span>
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  </button>
                )}
              </div>
            </div>
          )}

          {dispatchedAgent && (
            <div className="p-4 border-b border-border/30">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Comunicação Operacional com Agente</h3>
              {agentSceneAnalysis && Number(agentSceneAnalysis.eventId)===Number(dispatchedAgent.eventId) && <div className={`mb-2 p-2 rounded border text-[9px] ${agentSceneAnalysis.priority==='critical'?'bg-red-500/15 border-red-500/30 text-red-100':'bg-amber-500/10 border-amber-500/20 text-amber-100'}`}><strong>IA {String(agentSceneAnalysis.priority).toUpperCase()}:</strong> {agentSceneAnalysis.situation}<div className="text-white/60 mt-0.5">{agentSceneAnalysis.recommendation}</div></div>}
              <div className="max-h-24 overflow-auto space-y-1 mb-2">{agentRealtimeMessages.filter(m=>Number(m.eventId)===Number(dispatchedAgent.eventId)).slice(-6).map((m,i)=><div key={`${m.timestamp}-${i}`} className={`text-[9px] p-1.5 rounded ${m.kind==='signal'?'bg-amber-500/10 text-amber-100':'bg-white/5 text-white/75'}`}><strong>{m.agentName}:</strong> {m.message}</div>)}</div>
              <div className="flex gap-1"><input value={agentMessageDraft} onChange={e=>setAgentMessageDraft(e.target.value)} placeholder="Mensagem para o agente..." className="min-w-0 flex-1 px-2 py-1.5 rounded bg-black/20 border border-purple-500/25 text-[9px] text-white"/><button onClick={()=>{const message=agentMessageDraft.trim();if(!message||!socket)return;socket.emit('dashboard:message-to-agent',{eventId:dispatchedAgent.eventId,message,kind:'message'});setAgentMessageDraft('');}} className="px-2 py-1.5 rounded bg-purple-500/20 border border-purple-500/30 text-purple-100 text-[9px]">Enviar</button></div>
              <div className="grid grid-cols-2 gap-1 mt-1">{['Aceite e inicie deslocamento','Ative câmera ao chegar','Mantenha distância segura','Confirme pessoa localizada'].map(signal=><button key={signal} onClick={()=>socket?.emit('dashboard:message-to-agent',{eventId:dispatchedAgent.eventId,message:signal,kind:'signal'})} className="px-1.5 py-1 rounded bg-amber-500/10 border border-amber-500/20 text-amber-100 text-[8px]">{signal}</button>)}</div>
            </div>
          )}

          {/* Coordinates + Address */}
          {location && (
            <div className="p-4 border-b border-border/30">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Localização</h3>
              <LocationAddress lat={location.latitude} lng={location.longitude} />
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                <div className="p-1.5 rounded bg-secondary/50">
                  <div className="text-[9px] text-muted-foreground">Lat</div>
                  <div className="text-[10px] font-mono font-medium">{location.latitude.toFixed(6)}</div>
                </div>
                <div className="p-1.5 rounded bg-secondary/50">
                  <div className="text-[9px] text-muted-foreground">Lng</div>
                  <div className="text-[10px] font-mono font-medium">{location.longitude.toFixed(6)}</div>
                </div>
                <div className="p-1.5 rounded bg-secondary/50">
                  <div className="text-[9px] text-muted-foreground">Precisão</div>
                  <div className="text-[10px] font-mono font-medium">{location.accuracy.toFixed(0)}m</div>
                </div>
                <div className="p-1.5 rounded bg-secondary/50">
                  <div className="text-[9px] text-muted-foreground">Hora</div>
                  <div className="text-[10px] font-mono font-medium">
                    {new Date(location.timestamp).toLocaleTimeString("pt-BR")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recognition history */}
          {recognitionHistory.length > 0 && (
            <div className="border-b border-border/30">
              <div className="p-3 pb-1 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reconhecimentos</h3>
                <Badge variant="secondary" className="text-[9px]">{recognitionHistory.length}</Badge>
              </div>
              <ScrollArea className="max-h-32 px-3 pb-2">
                {recognitionHistory.map((rec, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/events/${rec.eventId}`)}
                    className="w-full flex items-center gap-2 py-1.5 hover:bg-secondary/30 rounded px-1 cursor-pointer text-left"
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${rec.matched ? "bg-green-500/20" : "bg-yellow-500/20"}`}>
                      {rec.matched ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <AlertTriangle className="w-3 h-3 text-yellow-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-medium truncate">
                        {rec.matched ? rec.personName : "Não identificado"}
                      </div>
                      <div className="text-[9px] text-muted-foreground">
                        Evento #{rec.eventId} — {new Date(rec.timestamp).toLocaleTimeString("pt-BR")}
                      </div>
                    </div>
                    <span className={`text-[9px] font-bold shrink-0 ${rec.confidence > 60 ? "text-green-400" : rec.confidence > 40 ? "text-yellow-400" : "text-muted-foreground"}`}>
                      {rec.confidence > 0 ? `${rec.confidence.toFixed(0)}%` : "—"}
                    </span>
                  </button>
                ))}
              </ScrollArea>
            </div>
          )}

          {/* V8.8: Emergency Alert Panel */}
          {mobileEmergency && (
            <div className="border-b border-red-500/30 bg-red-950/20">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-red-400">EMERGÊNCIA ATIVA</h3>
                  </div>
                  <button
                    onClick={() => { setMobileEmergency(null); setMobileEmergencyLocation(null); }}
                    className="text-[10px] text-red-400/60 hover:text-red-300 cursor-pointer"
                  >\u2715 Fechar</button>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs text-white font-medium">{mobileEmergency.reportedBy}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs text-white/70">{mobileEmergency.address || mobileEmergency.sector}</span>
                  </div>
                  {mobileEmergency.description && (
                    <div className="text-[10px] text-white/50 pl-5">{mobileEmergency.description}</div>
                  )}
                  {mobileEmergencyLocation && (
                    <div className="flex items-center gap-2 mt-1">
                      <Navigation className="w-3.5 h-3.5 text-green-400 animate-pulse" />
                      <span className="text-[10px] text-green-400 font-mono">
                        {mobileEmergencyLocation.lat.toFixed(6)}, {mobileEmergencyLocation.lng.toFixed(6)}
                        {mobileEmergencyLocation.accuracy > 0 && ` (±${mobileEmergencyLocation.accuracy.toFixed(0)}m)`}
                      </span>
                    </div>
                  )}
                  <div className="text-[9px] text-white/30 mt-1">
                    {new Date(mobileEmergency.timestamp).toLocaleTimeString('pt-BR')} — Evento #{mobileEmergency.eventId}
                  </div>
                  {/* V9.1: Google Maps link for emergency location */}
                  {mobileEmergencyLocation && (
                    <button
                      onClick={() => {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mobileEmergencyLocation.lat},${mobileEmergencyLocation.lng}`;
                        window.open(mapsUrl, '_blank');
                      }}
                      className="w-full mt-2 text-[10px] text-center py-2 rounded bg-green-500/15 border border-green-500/30 text-green-300 hover:bg-green-500/25 transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Navigation className="w-3.5 h-3.5" />
                      Abrir Rota no Google Maps
                    </button>
                  )}
                  {/* V9.5: Mobile camera feed */}
                  {mobileEmergencyCameraFrame && (
                    <div className="mt-2 rounded overflow-hidden border border-red-500/20">
                      <div className="flex items-center gap-1 bg-red-600/30 px-2 py-0.5">
                        <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        <span className="text-[9px] text-white font-bold">CÂMERA DA VÍTIMA - AO VIVO</span>
                      </div>
                      <img src={mobileEmergencyCameraFrame} alt="Feed" className="w-full h-32 object-cover" />
                    </div>
                  )}
                  {/* V9.5: Emergency audios received */}
                  {mobileEmergencyAudios.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <span className="text-[9px] text-amber-400 font-bold">🎤 Áudios Recebidos ({mobileEmergencyAudios.length})</span>
                      {mobileEmergencyAudios.slice(-3).map((a, i) => (
                        <audio key={i} controls className="w-full h-7" src={a.audioUrl} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* V9.0: App Activity Panel with Tabs — Check-ins & Acionamentos */}
          {(mobileCheckins.length > 0 || mobileEmergencies.length > 0 || activeVictims.length > 0 || activeTrips.length > 0) && (
            <div className="border-b border-border/30">
              <div className="p-3 pb-0">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Atividade do App</h3>
                <div className="flex gap-1 mb-2">
                  <button
                    onClick={() => setAppActivityTab('cameras')}
                    className={`flex-1 text-[10px] py-1.5 rounded cursor-pointer transition-colors ${
                      appActivityTab === 'cameras'
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30 font-bold'
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent'
                    }`}
                  >
                    Mosaico ({selectedCameraIds.length}/{ACTIVE_CAMERA_LIMIT})
                  </button>
                  <button
                    onClick={() => setAppActivityTab('trajetos')}
                    className={`flex-1 text-[10px] py-1.5 rounded cursor-pointer transition-colors ${
                      appActivityTab === 'trajetos'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-bold'
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent'
                    }`}
                  >
                    Trajetos ({activeTrips.length})
                  </button>
                  <button
                    onClick={() => setAppActivityTab('logadas')}
                    className={`flex-1 text-[10px] py-1.5 rounded cursor-pointer transition-colors ${
                      appActivityTab === 'logadas'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-bold'
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent'
                    }`}
                  >
                    Logadas ({activeVictims.length})
                  </button>
                  <button
                    onClick={() => setAppActivityTab('checkins')}
                    className={`flex-1 text-[10px] py-1.5 rounded cursor-pointer transition-colors ${
                      appActivityTab === 'checkins'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-bold'
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent'
                    }`}
                  >
                    Check-ins ({mobileCheckins.length})
                  </button>
                  <button
                    onClick={() => setAppActivityTab('emergencias')}
                    className={`flex-1 text-[10px] py-1.5 rounded cursor-pointer transition-colors ${
                      appActivityTab === 'emergencias'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30 font-bold'
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent'
                    }`}
                  >
                    Emergências ({mobileEmergencies.length})
                  </button>
                </div>
              </div>

              {appActivityTab === 'logadas' && (
                <ScrollArea className="max-h-64 px-3 pb-2">
                  <div className="mb-2 grid grid-cols-2 gap-1">
                    <button onClick={requestAllActiveCameras} disabled={activeVictims.length === 0} className="px-2 py-1.5 rounded bg-red-500/20 border border-red-500/35 text-[9px] font-bold text-red-100 disabled:opacity-40">Solicitar câmera de todos</button>
                    <button onClick={() => { const ranked = [...activeVictims].sort((a,b) => { const rank:any={critical:4,high:3,medium:2,low:1}; return (rank[activeSceneAnalyses[b.socketId]?.priority]||0)-(rank[activeSceneAnalyses[a.socketId]?.priority]||0); }); setActiveVictims(ranked); toast.success("Logados priorizados pela análise de risco disponível"); }} className="px-2 py-1.5 rounded bg-amber-500/15 border border-amber-500/30 text-[9px] font-bold text-amber-100">Priorizar por IA</button>
                  </div>
                  <div className="mb-2">
                    <input
                      value={activeVictimSearch}
                      onChange={(event) => setActiveVictimSearch(event.target.value)}
                      placeholder="Buscar pessoa ou setor..."
                      className="w-full text-[10px] px-2 py-1.5 rounded bg-black/20 border border-cyan-500/20 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
                    />
                  </div>
                  {activeVictims.filter(victim => {
                    const query = activeVictimSearch.trim().toLowerCase();
                    return !query || victim.name.toLowerCase().includes(query) || victim.sector.toLowerCase().includes(query);
                  }).length === 0 ? (
                    <p className="text-[10px] text-muted-foreground/50 text-center py-3">Nenhuma pessoa logada no momento</p>
                  ) : activeVictims.filter(victim => {
                    const query = activeVictimSearch.trim().toLowerCase();
                    return !query || victim.name.toLowerCase().includes(query) || victim.sector.toLowerCase().includes(query);
                  }).map((victim) => {
                    const selected = selectedActiveVictimId === victim.socketId;
                    return (
                      <button
                        key={victim.clientId || victim.socketId}
                        onClick={() => setSelectedActiveVictimId(victim.socketId)}
                        className={`w-full flex items-center gap-2 p-2 mb-1 rounded border text-left transition-colors cursor-pointer ${selected ? 'bg-cyan-500/15 border-cyan-400/50' : 'bg-cyan-950/10 border-cyan-500/15 hover:bg-cyan-500/10'}`}
                      >
                        {(victim.personPhotoUrl || victim.checkinPhotoUrl) ? (
                          <img src={victim.personPhotoUrl || victim.checkinPhotoUrl || ''} alt={victim.name} className="w-8 h-8 rounded-full object-cover border border-cyan-500/30" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-cyan-500/15 flex items-center justify-center border border-cyan-500/30"><User className="w-3.5 h-3.5 text-cyan-300" /></div>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block text-[10px] font-semibold text-cyan-100 truncate">{victim.name || 'Pessoa no App'}</span>
                          <span className="block text-[9px] text-muted-foreground truncate">{victim.sector || 'Setor não informado'} · conectou {new Date(victim.connectedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="block text-[8px] text-white/45 truncate">{victim.location ? `GPS ${victim.location.lat.toFixed(5)}, ${victim.location.lng.toFixed(5)}` : 'GPS aguardando'} · {victim.hasCheckin ? 'check-in realizado' : 'sem check-in'}</span>
                        </span>
                        <span className="text-[8px] text-green-300 shrink-0">ONLINE</span>
                      </button>
                    );
                  })}
                  {selectedActiveVictimId && (() => {
                    const victim = activeVictims.find(item => item.socketId === selectedActiveVictimId);
                    if (!victim) return null;
                    return (
                      <div className="mt-2 p-2 rounded border border-cyan-500/25 bg-cyan-950/15 text-[9px] space-y-1">
                        <div className="flex items-center justify-between"><strong className="text-cyan-200">Pessoa selecionada</strong><button onClick={() => setSelectedActiveVictimId(null)} className="text-white/50 hover:text-white cursor-pointer">Fechar</button></div>
                        <div className="text-white/75"><strong>Nome:</strong> {victim.name}</div>
                        <div className="text-white/75"><strong>Setor:</strong> {victim.sector}</div>
                        <div className="text-white/75"><strong>Sessão:</strong> {victim.sessionCode || victim.clientId || victim.socketId}</div>
                        <div className="text-white/75"><strong>Evento:</strong> {victim.eventId ? `#${victim.eventId}` : 'ainda não vinculado'}</div>
                        <div className="text-white/75"><strong>Última atualização:</strong> {new Date(victim.lastUpdate).toLocaleString('pt-BR')}</div>
                        <div className="flex gap-1 mt-2"><input value={victimMessageDraft} onChange={(event) => setVictimMessageDraft(event.target.value)} placeholder="Orientação para a pessoa..." className="min-w-0 flex-1 px-2 py-1.5 rounded bg-black/20 border border-pink-500/25 text-[9px] text-white placeholder:text-white/30" /><button onClick={() => { const message = victimMessageDraft.trim(); if (!message || !socket) return; socket.emit("dashboard:message-to-victim", { socketId: victim.socketId, clientId: victim.clientId, eventId: victim.eventId || null, message }); setVictimMessageDraft(""); toast.success("Orientação enviada ao aplicativo"); }} className="px-2 py-1.5 rounded border border-pink-400/40 bg-pink-500/20 text-pink-100 hover:bg-pink-500/30 cursor-pointer">Enviar</button></div>
                        <div className="grid grid-cols-2 gap-1 mt-1">{['Vá para local seguro','Agente a caminho','Compartilhe sua localização','Mantenha distância'].map(signal=><button key={signal} onClick={()=>socket?.emit("dashboard:message-to-victim",{socketId:victim.socketId,clientId:victim.clientId,eventId:victim.eventId||null,message:signal,kind:"signal"})} className="px-1.5 py-1 rounded bg-amber-500/10 border border-amber-500/20 text-[8px] text-amber-100">{signal}</button>)}</div>
                        <button onClick={() => requestActiveCamera(victim)} className="w-full mt-1 px-2 py-1.5 rounded bg-cyan-500/20 border border-cyan-500/30 text-cyan-100 hover:bg-cyan-500/30 cursor-pointer">Solicitar câmera + áudio</button>
                        <button onClick={() => toggleCameraSelection(victim.socketId)} className={`w-full px-2 py-1.5 rounded border cursor-pointer ${selectedCameraIds.includes(victim.socketId) ? 'bg-purple-500/20 border-purple-500/30 text-purple-200' : 'bg-white/5 border-white/10 text-white/70'}`}>
                          {selectedCameraIds.includes(victim.socketId) ? `Remover do mosaico (${selectedCameraIds.length}/${ACTIVE_CAMERA_LIMIT})` : `Adicionar ao mosaico (${selectedCameraIds.length}/${ACTIVE_CAMERA_LIMIT})`}
                        </button>
                        {activeCameraStatuses[victim.socketId] && <div className="text-cyan-200"><strong>Câmera:</strong> {activeCameraStatuses[victim.socketId]}</div>}
                        {activeSceneAnalyses[victim.socketId] && <div className={`p-1.5 rounded border ${activeSceneAnalyses[victim.socketId].priority === 'critical' ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-amber-500/25 bg-amber-500/5 text-amber-100'}`}><strong>IA:</strong> {activeSceneAnalyses[victim.socketId].priority.toUpperCase()} — {activeSceneAnalyses[victim.socketId].situation}<div className="text-[8px] mt-0.5">Ação: {activeSceneAnalyses[victim.socketId].recommendation}</div></div>}
                        {activeCameraFeeds.find(feed => feed.socketId === victim.socketId)?.frame && (
                          <div className="mt-1 rounded overflow-hidden border border-cyan-500/20">
                            <div className="px-2 py-0.5 bg-cyan-600/25 text-[8px] text-cyan-100">FEED DE {victim.name.toUpperCase()} — AO VIVO</div>
                            <img src={activeCameraFeeds.find(feed => feed.socketId === victim.socketId)?.frame || ''} alt={`Câmera de ${victim.name}`} className="w-full h-28 object-cover" />
                          </div>
                        )}
                        {activeCameraAudios.find(audio => audio.socketId === victim.socketId)?.audioUrl && (
                          <audio controls className="w-full h-7 mt-1" src={activeCameraAudios.find(audio => audio.socketId === victim.socketId)?.audioUrl} />
                        )}
                        {activeCameraAudios.find(audio => audio.socketId === victim.socketId)?.audioChunk && <div className="text-[8px] text-amber-200">Áudio ao vivo recebido desta sessão.</div>}
                      </div>
                    );
                  })()}
                </ScrollArea>
              )}

              {appActivityTab === 'cameras' && (
                <div className="px-3 pb-3">
                  {selectedCameraIds.length === 0 ? <p className="text-[10px] text-muted-foreground/50 text-center py-3">Selecione pessoas em Logadas para montar o mosaico</p> : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {selectedCameraIds.map((socketId) => {
                        const feed = activeCameraFeeds.find(item => item.socketId === socketId);
                        const victim = activeVictims.find(item => item.socketId === socketId);
                        return <div key={socketId} className="relative min-h-24 rounded border border-purple-500/25 bg-black/30 overflow-hidden">
                          {feed?.frame ? <img src={feed.frame} alt={`Feed de ${victim?.name || feed.reportedBy}`} className="w-full h-28 object-cover" /> : <div className="h-28 flex items-center justify-center text-[9px] text-white/50">Aguardando transmissão...</div>}
                          <div className="absolute top-0 left-0 right-0 px-1.5 py-1 bg-black/65 text-[8px] text-white truncate">{victim?.name || feed?.reportedBy || socketId}</div>
                          <button onClick={() => toggleCameraSelection(socketId)} className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-[8px] text-white/80 hover:text-white cursor-pointer">Remover</button>
                        </div>;
                      })}
                    </div>
                  )}
                </div>
              )}

              {appActivityTab === 'trajetos' && (
                <ScrollArea className="max-h-56 px-3 pb-2">
                  {activeTrips.length === 0 ? (
                    <p className="text-[10px] text-muted-foreground/50 text-center py-3">Nenhum trajeto protegido ativo</p>
                  ) : activeTrips.map((trip) => (
                    <div key={trip.tripId} className={`p-2 mb-1 rounded border ${trip.alertType ? 'bg-red-500/10 border-red-500/35' : 'bg-cyan-950/10 border-cyan-500/20'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-semibold text-cyan-100 truncate">{trip.reportedBy}</span>
                        <Badge className={`text-[8px] ${trip.alertType ? 'bg-red-500/20 text-red-300 border-red-500/30' : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'}`}>{trip.alertType ? trip.alertType.toUpperCase() : 'ATIVO'}</Badge>
                      </div>
                      <div className="text-[9px] text-muted-foreground truncate">→ {trip.destinationLabel}</div>
                      <div className="text-[8px] text-white/50 mt-0.5">{trip.travelMode === 'car' ? 'carro' : 'a pé'}{trip.distToDestMeters !== undefined ? ` · ${trip.distToDestMeters} m restantes` : ''}{trip.etaAt ? ` · ETA ${new Date(trip.etaAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
                      {trip.lat !== undefined && trip.lng !== undefined && <div className="text-[8px] text-green-300 mt-0.5">GPS {trip.lat.toFixed(5)}, {trip.lng.toFixed(5)}</div>}
                    </div>
                  ))}
                </ScrollArea>
              )}

              {appActivityTab === 'checkins' && (
                <ScrollArea className="max-h-48 px-3 pb-2">
                  {mobileCheckins.length === 0 ? (
                    <p className="text-[10px] text-muted-foreground/50 text-center py-3">Nenhum check-in registrado</p>
                  ) : mobileCheckins.map((ck, i) => (
                    <div key={i} className={`flex items-center gap-2 py-1.5 rounded px-1.5 mb-0.5 cursor-pointer hover:bg-secondary/30 transition-colors ${
                      ck.classification === 'suspect_identified' ? 'bg-red-500/5 hover:bg-red-500/10' : ''
                    }`}
                      onClick={() => {
                        if (ck.eventId) navigate(`/events/${ck.eventId}`);
                        else toast.info(`Check-in: ${ck.personName} — ${ck.sector} às ${new Date(ck.timestamp).toLocaleTimeString('pt-BR')}`);
                      }}
                    >
                      <div className={`w-8 h-8 rounded-full overflow-hidden shrink-0 border-2 ${
                        ck.classification === 'suspect_identified' ? 'border-red-500/50' : ck.matched ? 'border-green-500/30' : 'border-border/50'
                      }`}>
                        {(ck.personPhotoUrl || ck.capturedImageUrl) ? (
                          <img src={ck.personPhotoUrl || ck.capturedImageUrl || ''} alt={ck.personName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-secondary">
                            <User className="w-3.5 h-3.5 text-muted-foreground/40" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className={`text-[10px] font-medium truncate ${
                            ck.classification === 'suspect_identified' ? 'text-red-400 font-bold' : 'text-foreground'
                          }`}>
                            {ck.classification === 'suspect_identified' ? `\u26a0 ${ck.personName}` : ck.personName}
                          </span>
                          {ck.matched && ck.confidence > 0 && (
                            <span className="text-[9px] text-green-400 shrink-0">{ck.confidence.toFixed(0)}%</span>
                          )}
                        </div>
                        <div className="text-[9px] text-muted-foreground">
                          {ck.sector} — {new Date(ck.timestamp).toLocaleTimeString('pt-BR')}
                        </div>
                      </div>
                      {ck.classification === 'suspect_identified' && (
                        <Badge className="text-[8px] bg-red-500/20 text-red-400 border-red-500/30 shrink-0">AGRESSOR</Badge>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setMobileCheckins(prev => prev.filter((_, idx) => idx !== i)); toast.success('Check-in removido'); }}
                        className="text-muted-foreground/30 hover:text-red-400 cursor-pointer shrink-0 p-0.5"
                        title="Remover check-in"
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </ScrollArea>
              )}

              {appActivityTab === 'emergencias' && (
                <ScrollArea className="max-h-48 px-3 pb-2">
                  {mobileEmergencies.length === 0 ? (
                    <p className="text-[10px] text-muted-foreground/50 text-center py-3">Nenhuma emergência registrada</p>
                  ) : mobileEmergencies.map((em, i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5 rounded px-1.5 mb-0.5 cursor-pointer hover:bg-red-500/5 transition-colors bg-red-950/10"
                      onClick={() => {
                        if (em.eventId) navigate(`/events/${em.eventId}`);
                        else toast.info(`Emergência: ${em.reportedBy} — ${em.sector}`);
                      }}
                    >
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 border-2 border-red-500/30">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-bold text-red-400 truncate">{em.reportedBy}</span>
                          {em.agentName && (
                            <span className="text-[9px] text-purple-400 shrink-0">\u2192 {em.agentName}</span>
                          )}
                        </div>
                        <div className="text-[9px] text-muted-foreground">
                          {em.sector} — {new Date(em.timestamp).toLocaleTimeString('pt-BR')}
                        </div>
                        {em.description && <div className="text-[9px] text-white/40 truncate">{em.description}</div>}
                      </div>
                      <Badge className={`text-[8px] shrink-0 ${
                        em.status === 'agente_acionado' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30 animate-pulse'
                      }`}>
                        {em.status === 'agente_acionado' ? 'AGENTE ACIONADO' : 'ATIVO'}
                      </Badge>
                      <button
                        onClick={(e) => { e.stopPropagation(); setMobileEmergencies(prev => prev.filter((_, idx) => idx !== i)); toast.success('Emergência removida da lista'); }}
                        className="text-muted-foreground/30 hover:text-red-400 cursor-pointer shrink-0 p-0.5"
                        title="Remover da lista"
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </ScrollArea>
              )}
            </div>
          )}

          {/* V7.2 Módulo 4: Operational Metrics */}
          <div className="border-b border-border/30 p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Métricas Operacionais</h3>
            <MetricsPanel />
          </div>

          {/* Events log */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-3 pb-1 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Log de Eventos</h3>
              <Badge variant="secondary" className="text-[9px]">{events.length}</Badge>
            </div>
            <ScrollArea className="flex-1 px-3 pb-3">
              {events.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Nenhum evento registrado</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {events.map((evt, i) => (
                    <div key={i} className="flex items-start gap-2 py-1 group">
                      <div className="mt-0.5 shrink-0">{eventIcon(evt.eventType)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] leading-snug">{evt.message}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">
                          {new Date(evt.createdAt).toLocaleTimeString("pt-BR")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </div>
      {/* V8.3: App Install QR Code Modal */}
      {showAppQrModal && <AppInstallQrModal onClose={() => setShowAppQrModal(false)} />}

      {/* V7.4: Agent Camera Live Preview Modal */}
      {showAgentCameraModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowAgentCameraModal(false)}>
          <div className="relative w-[90vw] max-w-lg rounded-xl border border-cyan-500/30 bg-card overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 bg-cyan-500/5">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-semibold text-cyan-300">Câmera do Agente</span>
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                {agentCameraAgentName && (
                  <span className="text-[10px] text-muted-foreground">{agentCameraAgentName}</span>
                )}
                {agentCameraEventId && (
                  <Badge variant="secondary" className="text-[9px]">Evento #{agentCameraEventId}</Badge>
                )}
                <button onClick={() => setShowAgentCameraModal(false)} className="p-1 rounded hover:bg-secondary/50 cursor-pointer">
                  <XIcon className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </div>
            {/* Camera frame */}
            <div className="relative bg-black" style={{ minHeight: "300px" }}>
              {agentCameraFrame ? (
                <img
                  src={agentCameraFrame}
                  alt="Câmera do agente"
                  className="w-full h-auto object-contain"
                  style={{ maxHeight: "60vh" }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <Camera className="w-10 h-10 text-muted-foreground/30 mb-2" />
                  <span className="text-xs text-muted-foreground">Aguardando imagem da câmera...</span>
                </div>
              )}
              {/* Live indicator overlay */}
              <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded bg-black/60 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[10px] text-white font-medium">AO VIVO</span>
              </div>
            </div>
            {/* Modal footer */}
            <div className="px-4 py-2 border-t border-border/50 flex items-center justify-between">
              <span className="text-[9px] text-muted-foreground">
                Atualização a cada ~3 segundos
              </span>
              {agentCameraEventId && (
                <button
                  onClick={() => { setShowAgentCameraModal(false); navigate(`/events/${agentCameraEventId}`); }}
                  className="text-[10px] text-purple-400 hover:underline cursor-pointer"
                >
                  Ver Evento #{agentCameraEventId}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationAddress({ lat, lng }: { lat: number; lng: number }) {
  const { data } = trpc.geo.reverse.useQuery(
    { lat, lng },
    { staleTime: 60000, refetchOnWindowFocus: false }
  );

  if (!data || !data.address) return null;

  return (
    <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20 mb-1">
      <div className="flex items-start gap-2">
        <Navigation className="w-3.5 h-3.5 text-purple-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] font-medium text-purple-200 leading-snug">{data.address}</p>
          {data.sector && <p className="text-[9px] text-purple-300/60 mt-0.5">Setor: {data.sector}</p>}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ active, icon, label }: { active: boolean; icon: React.ReactNode; label: string }) {
  return (
    <div className={`flex flex-col items-center gap-0.5 py-1.5 rounded ${active ? "bg-green-500/10" : "bg-secondary/50"}`}>
      <div className={active ? "text-green-400" : "text-muted-foreground"}>{icon}</div>
      <span className={`text-[9px] font-medium ${active ? "text-green-400" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );
}

function SLATimer({ startTime, status }: { startTime: Date; status: string }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (status === 'resolved') {
      setElapsed(Math.floor((Date.now() - new Date(startTime).getTime()) / 1000));
      return;
    }
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - new Date(startTime).getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime, status]);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const isWarning = mins >= 5 && status !== 'resolved';
  const isCritical = mins >= 10 && status !== 'resolved';
  return (
    <div className={`flex items-center gap-2 p-2 rounded mb-2 ${isCritical ? 'bg-red-500/15 border border-red-500/30' : isWarning ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-secondary/30'}`}>
      <Clock className={`w-3.5 h-3.5 ${isCritical ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-muted-foreground'}`} />
      <div className="flex-1">
        <div className="text-[9px] text-muted-foreground uppercase tracking-wider">SLA — Tempo de Resposta</div>
        <div className={`text-sm font-mono font-bold ${isCritical ? 'text-red-400' : isWarning ? 'text-amber-400' : status === 'resolved' ? 'text-green-400' : 'text-foreground'}`}>
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </div>
      </div>
      {status === 'resolved' && <CheckCircle2 className="w-4 h-4 text-green-400" />}
      {isCritical && status !== 'resolved' && <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />}
    </div>
  );
}

function MetricsPanel() {
  const [, navigate] = useLocation();
  const { data: metrics } = trpc.opEvent.metrics.useQuery(undefined, {
    refetchInterval: 30000,
    staleTime: 15000,
  });

  if (!metrics) {
    return (
      <div className="grid grid-cols-3 gap-1.5">
        {[1,2,3].map(i => (
          <div key={i} className="p-2 rounded bg-secondary/30 animate-pulse">
            <div className="h-3 w-8 bg-secondary rounded mb-1" />
            <div className="h-2 w-12 bg-secondary rounded" />
          </div>
        ))}
      </div>
    );
  }

  const items = [
    { label: "Eventos Hoje", value: metrics.today.events, color: "text-blue-400", link: "/events" },
    { label: "Abertos", value: metrics.events.active, color: "text-amber-400", link: "/events?status=active" },
    { label: "Conflitos", value: metrics.events.conflicts, color: "text-red-400", link: "/events?status=conflicts" },
    { label: "Agentes", value: `${metrics.agents.available}/${metrics.agents.total}`, color: "text-green-400", link: "/agents" },
    { label: "Resolvidos", value: metrics.events.resolved, color: "text-emerald-400", link: "/events?status=resolved" },
    { label: "Tempo Médio", value: metrics.events.avgResponseTimeSec ? `${Math.round(metrics.events.avgResponseTimeSec / 60)}min` : "—", color: "text-purple-400", link: "/events" },
  ];

  return (
    <div className="grid grid-cols-3 gap-1.5">
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => navigate(item.link)}
          className="p-2 rounded bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer text-left"
        >
          <div className={`text-sm font-bold ${item.color}`}>{item.value}</div>
          <div className="text-[9px] text-muted-foreground">{item.label}</div>
        </button>
      ))}
    </div>
  );
}

function AppInstallQrModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = trpc.app.installQr.useQuery(undefined, {
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  // Fallback: generate QR client-side if server fails
  const [clientQr, setClientQr] = useState<string | null>(null);
  const appUrl = `${window.location.origin}/app`;

  useEffect(() => {
    if (!data?.qrCode) {
      QRCodeLib.toDataURL(appUrl, {
        width: 512,
        margin: 2,
        color: { dark: "#7c3aed", light: "#ffffff" },
        errorCorrectionLevel: "H",
      }).then(setClientQr).catch(() => {});
    }
  }, [data, appUrl]);

  const qrImage = data?.qrCode || clientQr;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-[90vw] max-w-sm rounded-xl border border-purple-500/30 bg-card overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-purple-500/5">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-purple-300">Baixar App ELLA</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary/50 cursor-pointer">
            <XIcon className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col items-center p-6">
          <div className="text-xs text-muted-foreground text-center mb-4">
            Escaneie o QR Code abaixo com a câmera do celular para instalar o aplicativo ELLA
          </div>

          {isLoading && !qrImage ? (
            <div className="w-48 h-48 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : qrImage ? (
            <div className="rounded-xl overflow-hidden bg-white p-3 mb-4 shadow-lg shadow-purple-500/10">
              <img src={qrImage} alt="QR Code App ELLA" className="w-48 h-48 object-contain" />
            </div>
          ) : (
            <div className="w-48 h-48 flex items-center justify-center bg-secondary/30 rounded-xl mb-4">
              <QrCode className="w-12 h-12 text-muted-foreground/30" />
            </div>
          )}

          <div className="text-center space-y-2">
            <p className="text-xs font-medium text-foreground">ELLA Command Platform</p>
            <p className="text-[10px] text-muted-foreground">
              Emergência, check-in facial, serviços e chamados em tempo real
            </p>
          </div>

          <div className="flex gap-2 mt-4 w-full">
            <button
              onClick={() => {
                navigator.clipboard.writeText(appUrl);
                toast.success("Link copiado!");
              }}
              className="flex-1 text-xs py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 hover:bg-purple-500/20 transition-colors cursor-pointer text-center"
            >
              📋 Copiar Link
            </button>
            <button
              onClick={() => {
                window.open(appUrl, '_blank');
              }}
              className="flex-1 text-xs py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 hover:bg-purple-500/20 transition-colors cursor-pointer text-center"
            >
              🔗 Abrir App
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-border/50 bg-secondary/20">
          <p className="text-[9px] text-muted-foreground text-center">
            Compatível com iPhone e Android — Adicione à tela inicial para usar como aplicativo
          </p>
        </div>
      </div>
    </div>
  );
}
